import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, X } from 'lucide-react';
import api from '../lib/api';

const DISMISS_KEY = 'mantle_trial_banner_dismissed_at';

/**
 * Subtle countdown banner shown across the app when the workspace
 * has fewer than 5 days left in its trial. Dismissable per-day.
 */
export default function TrialBanner() {
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/workspace');
        setWs(data);
      } catch (_) { /* unauthenticated or no workspace */ }
    })();
    try {
      const last = localStorage.getItem(DISMISS_KEY);
      if (last && (Date.now() - Number(last)) < 24 * 60 * 60 * 1000) setHidden(true);
    } catch {}
  }, []);

  if (!ws || hidden) return null;
  if (ws.plan !== 'trial') return null;
  const days = ws.trial_days_remaining;
  if (days == null || days > 5) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setHidden(true);
  };

  const urgent = days <= 2;

  return (
    <div data-testid="trial-banner"
      className={`relative w-full text-[13px] font-medium text-center px-4 py-2.5 ${
        urgent ? 'bg-red-500/12 text-red-300 border-b border-red-500/20' : 'bg-[#CBA33B]/10 text-[#CBA33B] border-b border-[#CBA33B]/20'
      }`}>
      <div className="flex items-center justify-center gap-2.5 flex-wrap">
        <AlertCircle size={14} className="shrink-0" />
        <span>
          {days === 0 ? 'Your free trial ends today.' :
           days === 1 ? 'Your free trial ends tomorrow.' :
           <>Your free trial ends in <strong className="font-bold">{days} days</strong>.</>}
        </span>
        <button onClick={() => navigate('/settings#team')} data-testid="trial-banner-upgrade"
          className="underline underline-offset-2 font-bold hover:opacity-80">
          See plans →
        </button>
      </div>
      <button onClick={dismiss} aria-label="Dismiss"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/10 opacity-60 hover:opacity-100">
        <X size={14} />
      </button>
    </div>
  );
}
